import { CalendarCheck } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { OneLine } from "@/components/ui/one-line";
import { useAchievements, useVisit } from "@/hooks/useEngagement";
import { AchievementIcon } from "@/components/engagement/Achievements";
import { followedAchievements, openAchievementsPage } from "@/components/engagement/AchievementsTray";
import { TIER_COLOR, achievementAmount, nextNeed } from "@/lib/engagement.core";

/****************
 * THE ACHIEVEMENTS I FOLLOW, ON THE DASHBOARD (Guillaume 10-27, round 8 #210:
 * "a small section in the Dashboard to display Achievement progression (the
 * ones I set)", web and phone, Pro and Lite). The same up-to-five as the
 * header trophy; with none followed, the way to pick them.
 * Phone twin: mobile-app/src/components/engagement/FollowedAchievements.tsx.
 ****************/
export function FollowedAchievements() {
  const { t } = useTranslation("learn");
  const q = useAchievements();
  if (q.isLoading || q.isError) return null;
  const rows = followedAchievements(q.data);
  return (
    <Card className="glass-card" data-testid="dash-followed-achievements">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-semibold">{t("ach.mineTitle")}</p>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={openAchievementsPage}>{t("ach.openPage")}</Button>
        </div>
        {rows.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t("ach.mineEmpty")}</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
            {rows.map((a) => {
              const need = nextNeed(a);
              return (
                <div key={a.key} className="flex items-center gap-2.5 rounded-lg border border-border/60 p-2.5 min-w-0">
                  <AchievementIcon a={a} size={22} />
                  <div className="min-w-0 flex-1">
                    <OneLine className="text-[13px] font-medium">{t(`ach.k.${a.key}.title`)}</OneLine>
                    {need !== null ? (
                      <>
                        <div className="h-1 rounded-full bg-white/10 mt-1 overflow-hidden"><div className="h-1 bg-primary" style={{ width: `${Math.min(100, (100 * (a.have ?? 0)) / need)}%` }} /></div>
                        <OneLine className="text-[11px] text-muted-foreground mt-0.5">{t("ach.next", { have: achievementAmount(a, a.have ?? 0), need: achievementAmount(a, need), tier: t(`ach.tier.${a.tier + 1}`) })}</OneLine>
                      </>
                    ) : <OneLine className="text-[11px]" style={{ color: TIER_COLOR[3] }}>{t("ach.max")}</OneLine>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The same, as one row inside Your Performance's facts (Guillaume, round 9 #302):
 * each followed achievement's icon and progress to its next tier; a click opens the page.
 */
export function FollowedAchievementsFact() {
  const { t } = useTranslation("learn");
  const q = useAchievements();
  if (q.isLoading || q.isError) return null;
  const rows = followedAchievements(q.data);
  // One under the other, each with its X / Y (Guillaume, round 10 #412).
  return (
    <button type="button" onClick={openAchievementsPage} data-testid="perf-followed-achievements"
      className="sm:col-span-2 rounded-lg border border-border/60 px-3 py-2 text-start hover:border-primary/40 transition-colors min-w-0 space-y-1.5">
      <span className="block text-muted-foreground">{t("ach.mineTitle")}</span>
      {rows.length === 0 ? (
        <span className="block text-xs text-muted-foreground">{t("ach.mineEmpty")}</span>
      ) : rows.map((a) => {
        const need = nextNeed(a);
        const pct = need === null ? 100 : Math.min(100, (100 * (a.have ?? 0)) / need);
        return (
          <span key={a.key} className="flex items-center gap-2 min-w-0">
            <AchievementIcon a={a} size={16} />
            <span className="min-w-0 flex-1 truncate text-[12px]">{t(`ach.k.${a.key}.title`)}</span>
            <span className="h-1 w-16 shrink-0 rounded-full bg-white/10 overflow-hidden">
              <span className="block h-1" style={{ width: `${pct}%`, background: need === null ? TIER_COLOR[3] : "hsl(var(--primary))" }} />
            </span>
            <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
              {need === null ? t("ach.max") : `${achievementAmount(a, a.have ?? 0)} / ${achievementAmount(a, need)}`}
            </span>
          </span>
        );
      })}
    </button>
  );
}

/**
 * Below Result + Equity, across both (Guillaume, round 11 #504): each followed
 * achievement on its own line with a LONG bar and its X / Y. A click opens the page.
 * Mounted in PortfolioSection with one line.
 */
export function FollowedAchievementsUnder() {
  const { t } = useTranslation("learn");
  const q = useAchievements();
  if (q.isLoading || q.isError) return null;
  const rows = followedAchievements(q.data);
  return (
    <button type="button" onClick={openAchievementsPage} data-testid="perf-followed-achievements"
      className="col-span-2 rounded-lg border border-border/60 px-3 py-2 text-start text-sm hover:border-primary/40 transition-colors min-w-0 space-y-1.5">
      <span className="block text-muted-foreground">{t("ach.mineTitle")}</span>
      {rows.length === 0 ? (
        <span className="block text-xs text-muted-foreground">{t("ach.mineEmpty")}</span>
      ) : rows.map((a) => {
        const need = nextNeed(a);
        const pct = need === null ? 100 : Math.min(100, (100 * (a.have ?? 0)) / need);
        return (
          <span key={a.key} className="flex items-center gap-2 min-w-0">
            <AchievementIcon a={a} size={16} />
            <span className="w-36 shrink-0 truncate text-[12px]">{t(`ach.k.${a.key}.title`)}</span>
            <span className="h-1.5 flex-1 min-w-[4rem] rounded-full bg-white/10 overflow-hidden">
              <span className="block h-1.5" style={{ width: `${pct}%`, background: need === null ? TIER_COLOR[3] : "hsl(var(--primary))" }} />
            </span>
            <span className="w-24 shrink-0 text-end truncate font-mono text-[11px] text-muted-foreground">
              {need === null ? t("ach.max") : `${achievementAmount(a, a.have ?? 0)} / ${achievementAmount(a, need)}`}
            </span>
          </span>
        );
      })}
    </button>
  );
}

/**
 * "X check-ins this week", under the facts (Guillaume, round 11 #505). The same
 * count the Today panel shows (the server's visits this week). One line to mount.
 */
export function CheckinsThisWeek() {
  const { t } = useTranslation("dashboard");
  const visit = useVisit();
  const n = visit.data?.checkins_this_week ?? 0;
  if (!visit.data) return null;
  return (
    <p className="sm:col-span-2 flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="perf-checkins">
      {/* The calendar that sat beside the Today panel's copy (removed 11-02). */}
      <CalendarCheck size={14} weight="fill" className="text-primary" />
      {t("today.checkins", { count: n })}
    </p>
  );
}
